import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

function Contact() {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [formData, setFormData] = useState({
    name: '', facility: '', phone: '', email: '',
    service: '', message: ''
  });
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && selected.size > 10 * 1024 * 1024) {
      setErrorMessage('File must be under 10MB.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setErrorMessage('');
    setFile(selected || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const data = new FormData();
      data.append('form-name', 'contact');
      data.append('bot-field', '');
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });
      if (file) {
        data.append('attachment', file);
      }

      const response = await fetch('/', {
        method: 'POST',
        body: data,
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', facility: '', phone: '', email: '', service: '', message: '' });
        setFile(null);
      } else {
        setStatus('error');
        setErrorMessage('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again or call us directly.');
    }
  };

  return (
    <main>
      {/* ===== PAGE HEADER ===== */}
      <section className="page-header">
        <div className="container">
          <div className="section-label" style={{ justifyContent: 'center' }}>Get In Touch</div>
          <h1 className="section-title fade-in">Contact & Order Exam</h1>
          <p className="section-subtitle fade-in fade-in-delay-1" style={{ margin: '0 auto' }}>
            Reach out to our team or submit an exam order. We're available 24/7
            to assist healthcare providers.
          </p>
        </div>
      </section>

      {/* ===== CONTACT SECTION ===== */}
      <section className="contact-section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="contact-grid">
            {/* Left side - Info */}
            <div>
              <div className="section-label">Contact Information</div>
              <h2 className="section-title" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
                We're Here to Help
              </h2>
              <p className="section-subtitle">
                Our team is available around the clock. Whether you need to order an exam,
                ask about insurance, or have general questions — we're ready to assist.
              </p>

              <div className="contact-info-cards">
                <a href="tel:8186265240" className="contact-info-card">
                  <div className="contact-icon-box">
                    <Phone />
                  </div>
                  <div>
                    <h4>Phone</h4>
                    <p>(818) 626-5240</p>
                  </div>
                </a>

                <a href="mailto:exams@pdicare.com" className="contact-info-card">
                  <div className="contact-icon-box">
                    <Mail />
                  </div>
                  <div>
                    <h4>Email</h4>
                    <p>exams@pdicare.com</p>
                  </div>
                </a>

                <div className="contact-info-card">
                  <div className="contact-icon-box">
                    <MapPin />
                  </div>
                  <div>
                    <h4>Service Area</h4>
                    <p>Los Angeles, CA</p>
                  </div>
                </div>

                <div className="contact-info-card">
                  <div className="contact-icon-box">
                    <Clock />
                  </div>
                  <div>
                    <h4>Availability</h4>
                    <p>24/7 — Always Open</p>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '32px' }}>
                <a href="tel:8186265240" className="btn btn-primary">
                  <Phone size={18} /> Call to Order Exam
                </a>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="contact-form">
              <h3>Send Us a Message</h3>

              {status === 'success' ? (
                <div className="form-success">
                  <p>Thank you! Your message has been sent successfully. We'll get back to you shortly.</p>
                </div>
              ) : (
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  encType="multipart/form-data"
                  onSubmit={handleSubmit}
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <p hidden>
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name</label>
                      <input
                        type="text" id="name" name="name"
                        placeholder="Dr. Jane Smith"
                        value={formData.name} onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="facility">Facility Name</label>
                      <input
                        type="text" id="facility" name="facility"
                        placeholder="Care Facility LLC"
                        value={formData.facility} onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel" id="phone" name="phone"
                        placeholder="(555) 123-4567"
                        value={formData.phone} onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email" id="email" name="email"
                        placeholder="doctor@facility.com"
                        value={formData.email} onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="service">Service Needed</label>
                    <select
                      id="service" name="service"
                      value={formData.service} onChange={handleChange}
                    >
                      <option value="">Select a service…</option>
                      <option value="Mobile X-Ray">Mobile X-Ray</option>
                      <option value="Ultrasound">Portable Ultrasound</option>
                      <option value="EKG">EKG / ECG</option>
                      <option value="Multiple">Multiple Services</option>
                      <option value="Other">Other / General Inquiry</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message" name="message"
                      placeholder="Tell us about your needs, patient details, or any questions…"
                      value={formData.message} onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="attachment">Attachment (optional)</label>
                    <input
                      type="file"
                      id="attachment"
                      name="attachment"
                      className="file-input"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                    />
                    <p className="file-hint">PDF, DOC, DOCX, JPG, or PNG — max 10MB</p>
                  </div>

                  {errorMessage && (
                    <p className="form-error">{errorMessage}</p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    disabled={status === 'submitting'}
                  >
                    <Send size={18} />
                    {status === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact;
